import { z } from "zod";
import { BlockIdSchema, ChapterIdSchema, ScriptVersionIdSchema } from "./ids";
import { WORD_CHANGE_MAX_LENGTH } from "./occurrences";
import { ChapterStateSchema } from "./states";
import { StyleSheetPutSchema } from "./stylesheet";

/*
 * A Word Change: the Author replaced one word or short name in a Block, and
 * the service offers the same replacement everywhere else the Book uses it.
 * Exact matches are found by code. Inflected forms ("Meiers" for "Meier") are
 * found by code and kept only when a yes/no model agrees they are the same
 * word and that the carried-over ending spells a real word.
 */

export const WordChangeSchema = z.strictObject({
  from: z.string().trim().min(1).max(WORD_CHANGE_MAX_LENGTH),
  to: z
    .string()
    .trim()
    .min(1)
    .max(WORD_CHANGE_MAX_LENGTH * 2),
});
export type WordChangePayload = z.infer<typeof WordChangeSchema>;

export const SnippetSchema = z.strictObject({
  before: z.string(),
  match: z.string(),
  after: z.string(),
  clippedStart: z.boolean(),
  clippedEnd: z.boolean(),
});

export const WordChangeSuggestionSchema = z.strictObject({
  blockId: BlockIdSchema,
  blockIndex: z.int().nonnegative(),
  /** UTF-16 offset of `found` in the Block's text (the draft text for the Chapter being edited). */
  offset: z.int().nonnegative(),
  /** What stands in the text: the changed word itself, or it with an ending. */
  found: z.string(),
  replacement: z.string(),
  kind: z.enum(["exact", "form"]),
  snippet: SnippetSchema,
});
export type WordChangeSuggestion = z.infer<typeof WordChangeSuggestionSchema>;

/**
 * Why a Chapter cannot take the change now: it is rendering or refining, it
 * has unapplied edits of its own, its script changed since the suggestions
 * were made, or it is in a state that takes no edits (failed).
 */
export const WordChangeBlockedSchema = z.enum([
  "rendering",
  "refining",
  "draft",
  "changed",
  "unavailable",
]);
export type WordChangeBlocked = z.infer<typeof WordChangeBlockedSchema>;

export const WordChangeChapterSchema = z.strictObject({
  chapterId: ChapterIdSchema,
  versionId: ScriptVersionIdSchema,
  state: ChapterStateSchema,
  /** The Chapter being edited: its suggestions go into the open draft, not through apply. */
  isSource: z.boolean(),
  /** Null when the change can be applied; "draft" when another Chapter has unapplied edits. */
  blocked: WordChangeBlockedSchema.nullable(),
  suggestions: z.array(WordChangeSuggestionSchema),
});
export type WordChangeChapter = z.infer<typeof WordChangeChapterSchema>;

/** The Style Sheet term whose spelling is the changed word; its pronunciation stays. */
export const WordChangeStyleSheetSchema = z.strictObject({
  baseVersion: z.int().positive(),
  termIndex: z.int().nonnegative(),
  term: z.string(),
  /** Each spelling in the entry that changes: the term itself or one of its forms. */
  renames: z.array(z.strictObject({ found: z.string(), replacement: z.string() })),
  /** The inflected forms accepted inside those spellings, sent back with the rename. */
  forms: z.array(z.string()),
  say_as: z.string(),
  ipa: z.string().nullable(),
  blocked: z.enum(["rendering", "draft"]).nullable(),
});
export type WordChangeStyleSheet = z.infer<typeof WordChangeStyleSheetSchema>;

/** POST /books/:id/word-changes/suggestions. */
export const WordChangeSuggestRequestSchema = z.strictObject({
  change: WordChangeSchema,
  /** The Chapter the edit was made in. */
  chapterId: ChapterIdSchema,
  /** That Chapter's unapplied draft edits, Block id to text, so its suggestions match what the Author sees. */
  draftEdits: z.record(z.uuid(), z.string()),
});
export type WordChangeSuggestRequest = z.infer<typeof WordChangeSuggestRequestSchema>;

export const WordChangeSuggestionsSchema = z.strictObject({
  change: WordChangeSchema,
  /** False when the inflected forms could not be checked; the exact matches are complete either way. */
  formsChecked: z.boolean(),
  /** Chapters with at least one suggestion, in Book order. */
  chapters: z.array(WordChangeChapterSchema),
  styleSheet: WordChangeStyleSheetSchema.nullable(),
});
export type WordChangeSuggestions = z.infer<typeof WordChangeSuggestionsSchema>;

/** POST /books/:id/word-changes/apply: the accepted suggestions outside the Chapter being edited. */
export const WordChangeApplyRequestSchema = z.strictObject({
  change: WordChangeSchema,
  chapters: z
    .array(
      z.strictObject({
        chapterId: ChapterIdSchema,
        versionId: ScriptVersionIdSchema,
        occurrences: z
          .array(
            z.strictObject({
              blockId: BlockIdSchema,
              offset: z.int().nonnegative(),
              found: z.string().min(1),
            }),
          )
          .min(1),
      }),
    )
    .max(500),
  styleSheet: z
    .strictObject({
      baseVersion: z.int().positive(),
      termIndex: z.int().nonnegative(),
      /** The inflected forms accepted with the suggestions ("Meiers"), renamed in the entry too. */
      forms: z.array(z.string().min(1)).max(100),
    })
    .nullable(),
});
export type WordChangeApplyRequest = z.infer<typeof WordChangeApplyRequestSchema>;

export const WordChangeApplyResultSchema = z.strictObject({
  chapters: z.array(
    z.strictObject({
      chapterId: ChapterIdSchema,
      /** Occurrences changed; 0 with a reason when the Chapter was skipped. */
      applied: z.int().nonnegative(),
      skipped: WordChangeBlockedSchema.nullable(),
      /** The new script version, when one was made. */
      version: z.int().positive().nullable(),
    }),
  ),
  styleSheet: z
    .strictObject({
      skipped: z.enum(["rendering", "draft", "changed"]).nullable(),
      version: z.int().positive().nullable(),
    })
    .nullable(),
});
export type WordChangeApplyResult = z.infer<typeof WordChangeApplyResultSchema>;

/** POST /books/:id/style-sheet/occurrences: where each term is spoken with its pronunciation. */
export const TermOccurrencesRequestSchema = z.strictObject({
  /** The unsaved Style Sheet draft; absent means the saved Style Sheet. */
  draft: StyleSheetPutSchema.optional(),
  /** The term whose places are listed; every other term gets its counts only. */
  detailTerm: z.int().nonnegative().optional(),
});
export type TermOccurrencesRequest = z.infer<typeof TermOccurrencesRequestSchema>;

export const TERM_OCCURRENCE_EXAMPLES = 3;

export const TermOccurrencesSchema = z.strictObject({
  terms: z.array(
    z.strictObject({
      termIndex: z.int().nonnegative(),
      total: z.int().nonnegative(),
      chapters: z.array(
        z.strictObject({
          chapterId: ChapterIdSchema,
          count: z.int().positive(),
          /** The first few places, for the Author to read. */
          examples: z.array(z.strictObject({ spelling: z.string(), snippet: SnippetSchema })),
        }),
      ),
    }),
  ),
});
export type TermOccurrences = z.infer<typeof TermOccurrencesSchema>;
